import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { KeyRound, Loader2, Check, Zap } from "lucide-react";

const BRMPINSetup = ({ onComplete }) => {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSetPIN = async () => {
    if (pin.length !== 4) {
      toast.error("PIN must be 4 digits");
      return;
    }
    if (pin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }

    setLoading(true);
    try {
      await authAPI.setPIN(pin);
      toast.success("PIN set successfully!");
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <section className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs">
        <div className="text-center pb-2">
          <div className="w-14 h-14 rounded-xl bg-brand-500 flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Set Your PIN</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a 4-digit PIN for quick login
          </p>
        </div>

        <div className="space-y-6 pt-4">
          <div className="space-y-2">
            <Label htmlFor="new-pin" className="text-sm font-medium text-gray-700">New PIN</Label>
            <Input
              id="new-pin"
              type="password"
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              maxLength={4}
              className="text-center text-2xl tracking-[1em] h-14 border-gray-200 focus:border-brand-400"
              data-testid="new-pin-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-pin" className="text-sm font-medium text-gray-700">Confirm PIN</Label>
            <Input
              id="confirm-pin"
              type="password"
              placeholder="••••"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              maxLength={4}
              className="text-center text-2xl tracking-[1em] h-14 border-gray-200 focus:border-brand-400"
              data-testid="confirm-pin-input"
            />
            {confirmPin.length === 4 && pin === confirmPin && (
              <p className="text-sm text-emerald-600 flex items-center gap-1 mt-1">
                <Check className="w-4 h-4" />
                PINs match
              </p>
            )}
          </div>

          <Button
            className="w-full h-12"
            onClick={handleSetPIN}
            disabled={loading || pin.length !== 4 || pin !== confirmPin}
            data-testid="set-pin-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Set PIN & Continue
          </Button>

          <p className="text-xs text-center text-gray-500">
            You'll use this PIN to login quickly next time
          </p>
        </div>
      </section>
    </div>
  );
};

export default BRMPINSetup;
